import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { $isLinkNode, LinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { $patchStyleText } from "@lexical/selection";
import { mergeRegister } from "@lexical/utils";
import Spinner from "components/Spinner";
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  TextNode,
  UNDO_COMMAND,
} from "lexical";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";

import { setNotification } from "features/Notification/notificationSlice";
import { addUser, editUser } from "features/user/userActions";
import SetPermissions from "./SetPermissions";

const signatureLexicalTheme = {
  paragraph: "signature-editor-paragraph",
  link: "signature-editor-link",
};

const FONT_FAMILIES = [
  { label: "Sans Serif", value: "Arial, Helvetica, sans-serif" },
  { label: "Serif", value: '"Times New Roman", Times, serif' },
  { label: "Fixed Width", value: '"Courier New", Courier, monospace' },
  { label: "Comic Sans", value: '"Comic Sans MS", cursive, sans-serif' },
];

const FONT_SIZE_OPTIONS = [
  { label: "Small", value: "12px" },
  { label: "Normal", value: "14px" },
  { label: "Large", value: "18px" },
  { label: "Huge", value: "24px" },
];

const DEFAULT_TEXT_COLOR = "#1f2937";
const DEFAULT_BG_COLOR = "transparent";

const patchStyleConversion = (originalDOMConverter) => (node) => {
  const original = originalDOMConverter?.(node);
  if (!original) {
    return null;
  }

  const originalOutput = original.conversion(node);
  if (!originalOutput) {
    return originalOutput;
  }

  const backgroundColor = node.style.backgroundColor;
  const color = node.style.color;
  const fontFamily = node.style.fontFamily;
  const fontWeight = node.style.fontWeight;
  const fontSize = node.style.fontSize;
  const textDecoration = node.style.textDecoration;

  return {
    ...originalOutput,
    forChild: (lexicalNode, parent) => {
      const output = originalOutput.forChild
        ? originalOutput.forChild(lexicalNode, parent)
        : lexicalNode;

      if ($isTextNode(output)) {
        const style = [
          backgroundColor ? `background-color: ${backgroundColor}` : null,
          color ? `color: ${color}` : null,
          fontFamily ? `font-family: ${fontFamily}` : null,
          fontWeight ? `font-weight: ${fontWeight}` : null,
          fontSize ? `font-size: ${fontSize}` : null,
          textDecoration ? `text-decoration: ${textDecoration}` : null,
        ]
          .filter(Boolean)
          .join("; ");

        if (style) {
          return output.setStyle(style);
        }
      }

      return output;
    },
  };
};

class ExtendedTextNode extends TextNode {
  static getType() {
    return "extended-text";
  }

  static clone(node) {
    return new ExtendedTextNode(node.__text, node.__key);
  }

  static importDOM() {
    const importers = TextNode.importDOM();

    return {
      ...importers,
      code: () => ({
        conversion: patchStyleConversion(importers?.code),
        priority: 1,
      }),
      em: () => ({
        conversion: patchStyleConversion(importers?.em),
        priority: 1,
      }),
      span: () => ({
        conversion: patchStyleConversion(importers?.span),
        priority: 1,
      }),
      strong: () => ({
        conversion: patchStyleConversion(importers?.strong),
        priority: 1,
      }),
      sub: () => ({
        conversion: patchStyleConversion(importers?.sub),
        priority: 1,
      }),
      sup: () => ({
        conversion: patchStyleConversion(importers?.sup),
        priority: 1,
      }),
      u: () => ({
        conversion: patchStyleConversion(importers?.u),
        priority: 1,
      }),
    };
  }

  isSimpleText() {
    return false;
  }
}

const signatureEditorConfig = {
  namespace: "user-signature-editor",
  theme: signatureLexicalTheme,
  nodes: [
    HeadingNode,
    QuoteNode,
    ListNode,
    ListItemNode,
    LinkNode,
    ExtendedTextNode,
    {
      replace: TextNode,
      with: (node) => new ExtendedTextNode(node.__text),
      withKlass: ExtendedTextNode,
    },
  ],
  onError(error) {
    console.error("Signature editor error", error);
  },
};

const normalizeUrl = (value) => {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^[a-zA-Z][\w+.-]*:/.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

const VALID_URL_SCHEMES = new Set(["http:", "https:", "mailto:", "tel:"]);

const validateLinkUrl = (value) => {
  if (!value) return false;
  try {
    const parsed = new URL(
      value,
      typeof window !== "undefined"
        ? window.location.origin
        : "http://localhost",
    );
    return VALID_URL_SCHEMES.has(parsed.protocol);
  } catch (error) {
    return false;
  }
};

const getStyleValue = (styleString, property) => {
  if (!styleString) return "";
  const regex = new RegExp(`${property}\\s*:\\s*([^;]+)`, "i");
  const match = styleString.match(regex);
  return match ? match[1].trim() : "";
};

const sanitizeHexColor = (value, fallback) => {
  if (!value) return fallback;
  const trimmed = value.trim();
  if (trimmed === "transparent") return fallback;
  return trimmed.startsWith("#") ? trimmed : fallback;
};

const sanitizeBgColor = (value) => {
  if (!value || value.trim() === "transparent") {
    return DEFAULT_BG_COLOR;
  }
  const trimmed = value.trim();
  return trimmed.startsWith("#") ? trimmed : DEFAULT_BG_COLOR;
};

const getSelectedNode = (selection) => {
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  return selection.isBackward() ? focusNode : anchorNode;
};

const SignaturePrefillPlugin = ({ contentHTML, loadKey }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (loadKey === null || loadKey === undefined) {
      return;
    }
    if (typeof window === "undefined" || typeof DOMParser === "undefined") {
      return;
    }

    editor.update(() => {
      const root = $getRoot();
      root.clear();

      if (contentHTML) {
        const parser = new DOMParser();
        const dom = parser.parseFromString(contentHTML, "text/html");
        const nodes = $generateNodesFromDOM(editor, dom);

        nodes.forEach((node) => {
          if ($isElementNode(node)) {
            root.append(node);
          } else if ($isTextNode(node)) {
            const paragraph = $createParagraphNode();
            paragraph.append(node);
            root.append(paragraph);
          }
        });
      }

      root.selectStart();
    });
  }, [contentHTML, editor, loadKey]);

  return null;
};

const SignatureEditorRefPlugin = ({ onReady }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    onReady(editor);
  }, [editor, onReady]);

  return null;
};

const SignatureToolbar = () => {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrike, setIsStrike] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [blockType, setBlockType] = useState("paragraph");
  const [elementFormat, setElementFormat] = useState("left");
  const [fontFamily, setFontFamily] = useState(FONT_FAMILIES[0].value);
  const [fontSize, setFontSize] = useState(FONT_SIZE_OPTIONS[1].value);
  const [textColor, setTextColor] = useState(DEFAULT_TEXT_COLOR);
  const [bgColor, setBgColor] = useState(DEFAULT_BG_COLOR);

  const applyStyleToSelection = (styles) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, styles);
      }
    });
  };

  const setFontSizeByIndex = (index) => {
    const boundedIndex = Math.max(
      0,
      Math.min(FONT_SIZE_OPTIONS.length - 1, index),
    );
    const newSize = FONT_SIZE_OPTIONS[boundedIndex].value;
    setFontSize(newSize);
    applyStyleToSelection({ "font-size": newSize });
  };

  const increaseFontSize = () => {
    const currentIndex = FONT_SIZE_OPTIONS.findIndex(
      (item) => item.value === fontSize,
    );
    setFontSizeByIndex((currentIndex < 0 ? 1 : currentIndex) + 1);
  };

  const decreaseFontSize = () => {
    const currentIndex = FONT_SIZE_OPTIONS.findIndex(
      (item) => item.value === fontSize,
    );
    setFontSizeByIndex((currentIndex < 0 ? 1 : currentIndex) - 1);
  };

  const applyLink = () => {
    if (isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      return;
    }
    const rawValue = window.prompt("Enter URL", "https://");
    if (rawValue === null) return;
    const normalized = normalizeUrl(rawValue);
    if (normalized && validateLinkUrl(normalized)) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, normalized);
    }
  };

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
      setIsStrike(selection.hasFormat("strikethrough"));

      const anchorNode = selection.anchor.getNode();
      const topLevel =
        anchorNode.getKey() === "root"
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();

      const elementType = topLevel.getType();
      const formatType =
        typeof topLevel.getFormatType === "function"
          ? topLevel.getFormatType()
          : "left";

      if (
        elementType === "list" &&
        typeof topLevel.getListType === "function"
      ) {
        setBlockType(topLevel.getListType());
      } else {
        setBlockType(elementType === "root" ? "paragraph" : elementType);
      }
      setElementFormat(formatType || "left");

      const node = getSelectedNode(selection);
      const parent = node ? node.getParent() : null;
      setIsLink($isLinkNode(node) || $isLinkNode(parent));

      if ($isTextNode(node)) {
        const style = node.getStyle();
        setFontFamily(
          getStyleValue(style, "font-family") || FONT_FAMILIES[0].value,
        );
        setFontSize(
          getStyleValue(style, "font-size") || FONT_SIZE_OPTIONS[1].value,
        );
        setTextColor(
          sanitizeHexColor(getStyleValue(style, "color"), DEFAULT_TEXT_COLOR),
        );
        setBgColor(sanitizeBgColor(getStyleValue(style, "background-color")));
      }
    }
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          editor.getEditorState().read(() => {
            updateToolbar();
          });
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, updateToolbar]);

  const toggleList = (command, type) => {
    if (blockType === type) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(command, undefined);
    }
  };

  return (
    <div className="signature-editor-toolbar">
      <button
        type="button"
        onMouseDown={(event) => event.preventDefault()}
        title="Undo"
        onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
      >
        ↺
      </button>
      <button
        type="button"
        onMouseDown={(event) => event.preventDefault()}
        title="Redo"
        onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
      >
        ↻
      </button>

      <select
        value={fontFamily}
        title="Font family"
        onChange={(event) => {
          const value = event.target.value;
          setFontFamily(value);
          applyStyleToSelection({ "font-family": value });
        }}
      >
        {FONT_FAMILIES.map((font) => (
          <option key={font.value} value={font.value}>
            {font.label}
          </option>
        ))}
      </select>

      <select
        value={fontSize}
        title="Font size"
        onChange={(event) => {
          const value = event.target.value;
          setFontSize(value);
          applyStyleToSelection({ "font-size": value });
        }}
      >
        {FONT_SIZE_OPTIONS.map((size) => (
          <option key={size.value} value={size.value}>
            {size.label}
          </option>
        ))}
      </select>

      <button
        type="button"
        onMouseDown={(event) => event.preventDefault()}
        title="Decrease font size"
        onClick={decreaseFontSize}
      >
        A-
      </button>
      <button
        type="button"
        onMouseDown={(event) => event.preventDefault()}
        title="Increase font size"
        onClick={increaseFontSize}
      >
        A+
      </button>

      <button
        type="button"
        className={isBold ? "active" : ""}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
      >
        B
      </button>
      <button
        type="button"
        className={isItalic ? "active" : ""}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
      >
        I
      </button>
      <button
        type="button"
        className={isUnderline ? "active" : ""}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")}
      >
        U
      </button>
      <button
        type="button"
        className={isStrike ? "active" : ""}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() =>
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough")
        }
      >
        S
      </button>

      <input
        type="color"
        title="Text color"
        value={textColor}
        onChange={(event) => {
          const value = event.target.value;
          setTextColor(value);
          applyStyleToSelection({ color: value });
        }}
      />
      <input
        type="color"
        title="Background color"
        value={bgColor === DEFAULT_BG_COLOR ? "#ffffff" : bgColor}
        onChange={(event) => {
          const value = event.target.value;
          setBgColor(value);
          applyStyleToSelection({ "background-color": value });
        }}
      />
      <button
        type="button"
        onMouseDown={(event) => event.preventDefault()}
        title="Clear background"
        onClick={() => {
          setBgColor(DEFAULT_BG_COLOR);
          applyStyleToSelection({ "background-color": "transparent" });
        }}
      >
        Bg×
      </button>

      <button
        type="button"
        className={blockType === "bullet" ? "active" : ""}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => toggleList(INSERT_UNORDERED_LIST_COMMAND, "bullet")}
      >
        • List
      </button>
      <button
        type="button"
        className={blockType === "number" ? "active" : ""}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => toggleList(INSERT_ORDERED_LIST_COMMAND, "number")}
      >
        1. List
      </button>

      <button
        type="button"
        className={elementFormat === "left" ? "active" : ""}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left")}
      >
        Left
      </button>
      <button
        type="button"
        className={elementFormat === "center" ? "active" : ""}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center")}
      >
        Center
      </button>
      <button
        type="button"
        className={elementFormat === "right" ? "active" : ""}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right")}
      >
        Right
      </button>

      <button
        type="button"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() =>
          editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)
        }
      >
        Indent-
      </button>
      <button
        type="button"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() =>
          editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)
        }
      >
        Indent+
      </button>

      <button
        type="button"
        className={isLink ? "active" : ""}
        onMouseDown={(event) => event.preventDefault()}
        onClick={applyLink}
      >
        Link
      </button>
    </div>
  );
};

const SignaturePlaceholder = () => (
  <div className="signature-editor-placeholder">Enter Email Signature</div>
);

function AddUser({ id, setShowAddUser, setSelectedUser, selectedUser }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.userInfo);

  const isAddMode = !id;

  const [locationData, setLocationData] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [defaultLocationList, setDefaultLocationList] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showPass, setShowPass] = useState(false);

  const [checkedItems, setCheckedItems] = useState([]);

  const [formData, setFormData] = useState({});
  const [signatureLoadKey, setSignatureLoadKey] = useState(0);
  const [signatureInitialHtml, setSignatureInitialHtml] = useState("");
  const signatureEditorRef = useRef(null);
  const signatureEditorInitialConfig = useMemo(() => signatureEditorConfig, []);

  const cleanEmailSignature = (signature) => {
    if (!signature || typeof signature !== "string") return "";
    const cleaned = signature.trim();
    return cleaned === "<p><br></p>" ? "" : cleaned;
  };

  const getLatestSignatureHtml = () => {
    let latestHtml = formData?.emailSignature || "";
    const editor = signatureEditorRef.current;

    if (editor) {
      editor.getEditorState().read(() => {
        latestHtml = $generateHtmlFromNodes(editor, null);
      });
    }

    return latestHtml;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const latestSignatureHtml = cleanEmailSignature(getLatestSignatureHtml());

    // console.log("formData", formData);

    dispatch(
      isAddMode
        ? addUser({
            ...formData,
            emailSignature: latestSignatureHtml,
            locationId: JSON.stringify(selectedLocation),
            permissions: checkedItems,
            defaultLocation:
              formData?.defaultLocation === "All brands"
                ? null
                : formData?.defaultLocation,

            defaultJobBoardLocation:
              formData?.defaultJobBoardLocation === "All brands"
                ? null
                : formData?.defaultJobBoardLocation,
          })
        : editUser({
            formData: {
              ...formData,
              emailSignature: latestSignatureHtml,
              locationId: JSON.stringify(selectedLocation),
              permissions: checkedItems,
              defaultLocation:
                formData?.defaultLocation === "All brands"
                  ? null
                  : formData?.defaultLocation,
              defaultJobBoardLocation:
                formData?.defaultJobBoardLocation === "All brands"
                  ? null
                  : formData?.defaultJobBoardLocation,
            },
            userId: formData?.id,
          }),
    ).then((res) => {
      if (res.type === "user/add/rejected") {
        setError(res.payload);
      } else {
        setShowAddUser(false);
        setSelectedUser(null);
      }
    });
  };

  const getLocations = async () => {
    try {
      const { data } = await API.get("/locations", { headers: authHeader() });
      setLocationData(data);
    } catch (err) {
      alert(err.message);
    }
  };

  useEffect(() => {
    getLocations();
    // API.get("permissions", { headers: authHeader() }).then(({ data }) => {
    //   setRawPermissions(data);
    //   const groupedPermNames = groupByFirstWord(data);
    //   setGroupPermissions(groupedPermNames);
    // });

    if (!isAddMode) {
      API.get(`users/${id}`, { headers: authHeader() }).then(({ data }) => {
        setFormData({ ...data });
        setSignatureInitialHtml(data?.emailSignature || "");
        setSignatureLoadKey((prev) => prev + 1);
        setSelectedLocation(
          data?.locationId ? JSON.parse(data?.locationId) : [],
        );
        setCheckedItems(
          typeof data.permissions === "string"
            ? JSON.parse(data.permissions)
            : data.permissions || [],
        );
        setIsLoading(false);
      });
    }
  }, []);

  useEffect(() => {
    dispatch(
      setNotification({
        message: error,
        type: "error",
      }),
    );
  }, [error]);

  useEffect(() => {
    if (locationData) {
      // add locations from locationData which are in selectedLocation list
      if (user?.roles === "admin") {
        setDefaultLocationList(locationData);
      } else {
        const defaultLocation = locationData.filter((item) => {
          return selectedLocation?.includes(item.id);
        });
        setDefaultLocationList(defaultLocation);
      }
    }
  }, [locationData, selectedLocation]);

  if (isLoading && !isAddMode) return <Spinner />;

  return (
    <>
      <div className="row">
        <div className="col-12 mb-2">
          <h3>{isAddMode ? "Add" : "Edit"} User</h3>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/*   Form Inputs
                 ********************************************* */}
                <div className="col-6">
                  <div className="row mb-2">
                    <div className="col-6">
                      <label className="form-label">First Name</label>
                      <input
                        type="text"
                        required
                        className={`form-control ${
                          formData?.firstName ? "" : "border-danger"
                        }`}
                        placeholder="Enter First Name"
                        value={formData?.firstName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            firstName: e.target.value,
                          })
                        }
                      />
                      <small className="text-danger">
                        {formData?.firstName == null ||
                        formData?.firstName === undefined ||
                        formData?.firstName === ""
                          ? "First Name is required"
                          : ""}
                      </small>
                    </div>
                    <div className="col-6">
                      <label className="form-label">Last Name</label>
                      <input
                        type="text"
                        required
                        className={`form-control ${
                          formData?.lastName ? "" : "border-danger"
                        }`}
                        placeholder="Enter Last Name"
                        value={formData?.lastName}
                        onChange={(e) =>
                          setFormData({ ...formData, lastName: e.target.value })
                        }
                      />
                      <small className="text-danger">
                        {formData?.lastName == null ||
                        formData?.lastName === undefined ||
                        formData?.lastName === ""
                          ? "Last Name is required"
                          : ""}
                      </small>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-6">
                      <label className="form-label">Phone</label>
                      <input
                        placeholder="Enter Phone"
                        type="tel"
                        name="phone"
                        className={`form-control ${
                          formData?.phone == null ||
                          formData?.phone === undefined ||
                          formData?.phone === ""
                            ? "border-danger"
                            : ""
                        }`}
                        required
                        value={formData?.phone}
                        onChange={(e) =>
                          setFormData({ ...formData, phone: e.target.value })
                        }
                      />
                      <small className="text-danger">
                        {formData?.phone == null ||
                        formData?.phone === undefined ||
                        formData?.phone === ""
                          ? "Phone is required"
                          : ""}
                      </small>
                    </div>
                    <div className="col-6">
                      <label className="form-label">Extention</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Extention"
                        value={formData?.phoneExt}
                        onChange={(e) =>
                          setFormData({ ...formData, phoneExt: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      required
                      className={`form-control ${
                        formData?.email ? "" : "border-danger"
                      }`}
                      placeholder="Enter Email"
                      value={formData?.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                    />
                    <small className="text-danger">
                      {formData?.email == null ||
                      formData?.email === undefined ||
                      formData?.email === ""
                        ? "Email is required"
                        : ""}
                    </small>
                  </div>
                  <div className="mb-2 ">
                    <label className="form-label">Password</label>
                    <div className="input-group">
                      <input
                        type={showPass ? "text" : "password"}
                        required={isAddMode}
                        className={`form-control ${
                          formData?.email ? "" : "border-danger"
                        }`}
                        placeholder="Enter password"
                        onChange={(e) =>
                          setFormData({ ...formData, password: e.target.value })
                        }
                      />
                      <span
                        onClick={() => setShowPass(!showPass)}
                        className="input-group-text input-group-text"
                      >
                        <i
                          className={`mdi mdi-eye${
                            showPass ? "" : "-off"
                          } fs-4`}
                        ></i>
                      </span>
                    </div>
                    <small className="text-danger">
                      {(formData?.password == null ||
                        formData?.password === undefined ||
                        formData?.password === "") &&
                      isAddMode
                        ? "password is required"
                        : ""}
                    </small>
                  </div>
                  <div className="mb-2 ">
                    <label className="form-label">Default Brand</label>
                    <div className="input-group">
                      <select
                        value={formData?.defaultLocation}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            defaultLocation: e.target.value,
                          })
                        }
                        className="form-control"
                      >
                        <option value={null}>All brands</option>
                        {defaultLocationList?.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.locationName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="mb-2 ">
                    <label className="form-label">
                      Default JobBoard Brand
                    </label>
                    <div className="input-group">
                      <select
                        value={formData?.defaultJobBoardLocation}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            defaultJobBoardLocation: e.target.value,
                          })
                        }
                        className="form-control"
                      >
                        <option value={null}>All brands</option>
                        {defaultLocationList?.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.locationName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <hr />
                  {/* <div className="mb-3 mt-3">
                    <h3 className="">Add email account ( Gmail ) </h3>
                    <a
                      target="_blank"
                      rel="noreferrer"
                      referrerPolicy="no-referrer"
                      href="https://myaccount.google.com/apppasswords?continue=https://myaccount.google.com/data-and-privacy?hl%3Den%26rapt%3DAEjHL4N-_-wqowUOflRj8zsWBHL93KyUfugluv0j26WMDbVjZEyajo8p5u5lV5u92pylN3k8u3XYEaG_8sR-5AvtsqDp5s1ezPxGXSrsfZ0TRSsAWLzqC7o%26utm_source%3DOGB%26utm_medium%3Dact%26gar%3DWzI0Nl0&pli=1&rapt=AEjHL4OYovdomiZLn4-l-Jfd38P2Dm1untWoxDqkS96MKXuQQHJMrU3kcGmmLc4mH9-qGBpfHarlQUPd1ozfKg9MJQvez1jIhWXqvO-VQaloqWJLsQPzQqw"
                    >
                      Click here to get your gmail smtp password
                    </a>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="smtpName" className="form-label">
                      Smtp Name
                    </label>
                    <input
                      className="form-control"
                      type="text"
                      id="smtpName"
                      placeholder="Enter smtp name"
                      value={formData?.smtpName || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, smtpName: e.target.value })
                      }
                      name="smtpName"
                    />
                  </div>{" "}
                  <div className="mb-3">
                    <label htmlFor="smtpEmail" className="form-label">
                      Smtp email
                    </label>
                    <input
                      className="form-control"
                      type="text"
                      id="smtpEmail"
                      placeholder="Enter smtp email"
                      name="smtpEmail"
                      value={formData?.smtpEmail || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, smtpEmail: e.target.value })
                      }
                    />
                  </div>{" "}
                  <div className="mb-3">
                    <label htmlFor="smtpPassword" className="form-label">
                      Smtp password
                    </label>
                    <input
                      className="form-control"
                      type="password"
                      id="smtpPassword"
                      placeholder="Enter smtp password"
                      name="smtpPassword"
                      value={formData?.smtpPassword || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          smtpPassword: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Email Signature</label>
                    <div
                      style={{
                        border: "1px solid #ced4da",
                        borderRadius: "0.25rem",
                        backgroundColor: "#fff",
                      }}
                    >
                      <LexicalComposer
                        key={signatureLoadKey}
                        initialConfig={signatureEditorInitialConfig}
                      >
                        <SignatureEditorRefPlugin
                          onReady={(editor) => {
                            signatureEditorRef.current = editor;
                          }}
                        />
                        <SignatureToolbar />
                        <div className="signature-editor-container">
                          <RichTextPlugin
                            contentEditable={
                              <ContentEditable className="signature-editor-input" />
                            }
                            placeholder={<SignaturePlaceholder />}
                            ErrorBoundary={LexicalErrorBoundary}
                          />
                          <SignaturePrefillPlugin
                            contentHTML={signatureInitialHtml}
                            loadKey={signatureLoadKey}
                          />
                          <HistoryPlugin />
                          <ListPlugin />
                          <LinkPlugin />
                          <OnChangePlugin
                            onChange={(editorState, editor) => {
                              editorState.read(() => {
                                const html = $generateHtmlFromNodes(editor, null);
                                setFormData((prev) => ({
                                  ...prev,
                                  emailSignature: html,
                                }));
                              });
                            }}
                          />
                        </div>
                      </LexicalComposer>
                    </div>
                    <small className="text-muted">
                      This signature will be added to the user's emails
                    </small>
                    <style>
                      {`
                        .signature-editor-toolbar {
                          display: flex;
                          gap: 6px;
                          flex-wrap: wrap;
                          border-bottom: 1px solid #ced4da;
                          background: #f8f9fa;
                          padding: 6px;
                        }
                        .signature-editor-toolbar button {
                          border: 1px solid #d0d7de;
                          border-radius: 4px;
                          background: #ffffff;
                          padding: 2px 8px;
                          font-size: 12px;
                          line-height: 1.4;
                        }
                        .signature-editor-toolbar select,
                        .signature-editor-toolbar input[type="color"] {
                          border: 1px solid #d0d7de;
                          border-radius: 4px;
                          background: #ffffff;
                          height: 28px;
                          padding: 0 6px;
                          font-size: 12px;
                        }
                        .signature-editor-toolbar button.active {
                          background: #e9f2ff;
                          border-color: #9ec5fe;
                        }
                        .signature-editor-container {
                          position: relative;
                        }
                        .signature-editor-input {
                          min-height: 150px;
                          max-height: 300px;
                          overflow-y: auto;
                          background-color: #fff;
                          padding: 10px 12px;
                          outline: none;
                        }
                        .signature-editor-placeholder {
                          position: absolute;
                          top: 10px;
                          left: 12px;
                          color: #6c757d;
                          pointer-events: none;
                        }
                        .signature-editor-input .signature-editor-paragraph {
                          margin: 0;
                        }
                        .signature-editor-link {
                          color: #0d6efd;
                          text-decoration: underline;
                        }
                        .signature-editor-input ul,
                        .signature-editor-input ol {
                          margin: 0;
                          padding-left: 22px;
                        }
                        .signature-editor-input p,
                        .signature-editor-input li {
                          line-height: 1.4;
                          word-wrap: break-word;
                          overflow-wrap: break-word;
                        }
                      `}
                    </style>
                  </div> */}
                  <div className="mb-2 ">
                    <label className="form-label">User IP</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter User IP"
                      value={formData?.userIp || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, userIp: e.target.value })
                      }
                    />
                  </div>
                </div>
                {/*   Permissions
                 ********************************************* */}
                {/* {selectedUser && selectedUser.role !== "admin"  && ( */}
                {(isAddMode ||
                  (user?.roles === "admin" &&
                    selectedUser?.id !== user?.id)) && (
                  <div
                    className="col-6"
                    style={{ height: "85vh", overflowY: "auto" }}
                  >
                    <label htmlFor="projectname" className="mb-0">
                      Assign Permissions
                    </label>
                    <p className="text-muted font-14">
                      Assign Module Permission to user
                    </p>

                    <SetPermissions
                      checkedItems={checkedItems}
                      setCheckedItems={setCheckedItems}
                      selectedLocation={selectedLocation}
                      locationData={locationData}
                      setSelectedLocation={setSelectedLocation}
                    />
                  </div>
                )}
                {/*   Buttons
                 ********************************************* */}
                <div className="d-flex mt-3">
                  <div
                    type="button"
                    className="btn me-2 btn-secondary ms-auto"
                    onClick={() => {
                      setShowAddUser(false);
                      setSelectedUser(null);
                    }}
                  >
                    Cancel
                  </div>

                  <button type="submit" className="btn btn-primary">
                    <i className="mdi mdi-account-check-outline me-1"></i>
                    {isAddMode ? "Add" : "Update"} user
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddUser;
